import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useDispatch } from 'react-redux';
import { clearIncidentPopups, resetIncident, SHOW_INCIDENT_REPORTED_POPUP } from '~/actions';
import { showErrorMessage } from '~/domain/utils';
import { apiMethods } from '~/services/api';
import { apiMethodsConst } from '~/services/methods';

export interface IReportIncidentPayload {
  jobId: string;
  incidentType: string;
}

type TReportIncidentResponse = {
  id?: string;
  [key: string]: unknown;
};
type TReportIncidentError = AxiosError<{ message?: string; error?: string; title?: string }>;

/**
 * Report-incident mutation for the Today list. Owns the incident API request
 * and the Redux popup transitions the saga used to drive: on success it bridges
 * the reported job's `todayJobs[].incident` flag (still Redux state read by the
 * Today table) and advances the popup state machine to the reported-success
 * screen; on failure it surfaces the error and clears/resets the popup state.
 */
export function useReportIncidentMutation() {
  const dispatch = useDispatch();

  return useMutation<TReportIncidentResponse, TReportIncidentError, IReportIncidentPayload>({
    mutationFn: async ({ jobId, incidentType }) => {
      return await apiMethods[apiMethodsConst.REPORT_INCIDENT_REQUEST]({
        jobId,
        type: incidentType,
      });
    },
    onSuccess: (response) => {
      // Bridge: mark the reported job's Redux Today-list row as having an incident.
      dispatch({ type: apiMethodsConst.REPORT_INCIDENT_REQUEST, payload: response });
      dispatch({ type: SHOW_INCIDENT_REPORTED_POPUP });
    },
    onError: (error) => {
      showErrorMessage({ error });
      dispatch(clearIncidentPopups());
      dispatch(resetIncident());
    },
  });
}
